import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Download, ShieldCheck, ShieldAlert } from "lucide-react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { PageHead, downloadFile, fmtDateTime } from "../components/dpa/dpaUtils";
import { RegisterTab } from "../components/governance/RegisterTab";
import { REGISTER_CONFIGS } from "../components/governance/registerConfigs";

const Tile = ({ n, label, tone, testId }) => (
  <div className={`border p-4 bg-white ${n > 0 && tone ? tone : ""}`} style={{ borderColor: "var(--hi-border)" }} data-testid={testId}>
    <div className="font-heading font-black text-3xl">{n}</div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
  </div>
);

const Overview = () => {
  const [o, setO] = useState(null);
  useEffect(() => { api.get("/governance/overview").then(({ data }) => setO(data)); }, []);
  if (!o) return null;
  return (
    <div className="space-y-5" data-testid="gov-overview">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Tile n={o.high_priority_open} label="High-priority requirements open" tone="border-l-4 border-l-orange-500" testId="gov-tile-high" />
        <Tile n={o.evidence_gaps} label="Requirements without evidence" tone="border-l-4 border-l-amber-500" testId="gov-tile-evidence" />
        <Tile n={o.risks_open_high} label="Open high / critical risks" tone="border-l-4 border-l-red-600" testId="gov-tile-risks" />
        <Tile n={o.dpia_outstanding} label="DPIA screenings outstanding" testId="gov-tile-dpia" />
        <Tile n={o.subprocessors_unreviewed} label="Suppliers without due diligence" testId="gov-tile-suppliers" />
        <Tile n={o.incidents_open} label="Open incidents / vulnerabilities" tone="border-l-4 border-l-red-600" testId="gov-tile-incidents" />
        <Tile n={o.notifications_overdue} label="Controller notices overdue" tone="bg-red-50" testId="gov-tile-notices" />
        <Tile n={o.reviews_overdue} label="Reviews overdue" testId="gov-tile-reviews" />
        <Tile n={o.dpa.without_dpa} label="Organisations without a DPA" tone="border-l-4 border-l-amber-500" testId="gov-tile-dpa" />
        <Tile n={o.dpa.awaiting_signature} label="DPAs awaiting signature" testId="gov-tile-dpa-sign" />
      </div>
      <Card className="rounded-none p-6">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Requirements by status ({o.requirements_total})</div>
        <div className="grid md:grid-cols-4 gap-2 text-sm">
          {Object.entries(o.requirements_by_status).map(([s, n]) => <div key={s} className="flex justify-between border-b py-1" style={{ borderColor: "var(--hi-border)" }}><span>{s}</span><b>{n}</b></div>)}
        </div>
        <p className="text-xs text-muted-foreground mt-3">{o.methodology} <Link to="/compliance" className="text-blue-700">Open DPA management →</Link></p>
      </Card>
    </div>
  );
};

const Report = () => {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/governance/report").then(({ data }) => setR(data)); }, []);
  if (!r) return null;
  return (
    <Card className="rounded-none p-6 space-y-4" data-testid="gov-report">
      <div className="flex items-center justify-between"><div className="font-semibold">{r.title}</div>
        <Button variant="outline" className="rounded-none" onClick={() => downloadFile("/governance/report/pdf", "discovery-gap-assessment.pdf")} data-testid="gov-report-pdf-btn"><Download className="h-4 w-4 mr-1" />PDF (with gap assessment)</Button></div>
      <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 p-2">Draft for owner review — not legal advice. Facts were read from the code and configuration; unknowns are listed in section 7.</div>
      {r.sections.map(([t, lines]) => (
        <div key={t}><div className="font-semibold text-sm mt-2">{t}</div>
          <ul className="list-disc ml-5 text-sm space-y-1 mt-1">{lines.map((l) => <li key={l}>{l}</li>)}</ul></div>
      ))}
    </Card>
  );
};

const Audit = () => {
  const [d, setD] = useState(null);
  const [v, setV] = useState(null);
  useEffect(() => { api.get("/governance/audit").then(({ data }) => setD(data.items)); api.get("/governance/audit/verify").then(({ data }) => setV(data)); }, []);
  if (!d) return null;
  return (
    <Card className="rounded-none p-6" data-testid="gov-audit">
      {v && <div className={`text-sm flex items-center gap-2 mb-3 ${v.ok ? "text-emerald-700" : "text-red-700"}`} data-testid="gov-audit-integrity">
        {v.ok ? <ShieldCheck className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}
        {v.ok ? `Integrity check passed — ${v.checked} events, hash chain unbroken` : `Integrity check FAILED at event #${v.broken_at_seq}`}</div>}
      {d.map((e) => (
        <div key={e.seq} className="text-xs border-t py-2" style={{ borderColor: "var(--hi-border)" }}>
          <span className="font-mono text-muted-foreground">#{e.seq} {fmtDateTime(e.at)}</span> · <b>{e.event.replace(/_/g, " ")}</b>{e.ref && <span className="font-mono"> · {e.ref}</span>} · {e.actor}
          {e.after && <div className="text-muted-foreground truncate" title={JSON.stringify(e.after)}>{Object.entries(e.after).map(([k, val]) => `${k}: ${typeof val === "object" ? JSON.stringify(val) : val}`).join(" · ")}</div>}
        </div>
      ))}
    </Card>
  );
};

const TABS = [["overview", "Overview"], ["report", "Discovery report"], ["requirements", "Requirements & evidence"], ["risks", "Risks"],
  ["dpia", "DPIA screening"], ["subprocessors", "Suppliers & integrations"], ["incidents", "Incidents & vulnerabilities"], ["audit", "Audit trail"]];

export default function GovernancePage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  return (
    <div className="p-8 space-y-6 fade-up" data-testid="governance-page">
      <PageHead kicker="Super Admin · Compliance" title="Governance Centre"
        sub="Obligations, evidence, risks and incidents for this application. Everything starts pre-filled from a code inspection — confirm or correct each item." />
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="rounded-none flex-wrap h-auto">
          {TABS.map(([k, l]) => <TabsTrigger key={k} value={k} className="rounded-none" data-testid={`gov-tab-${k}`}>{l}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="overview" className="mt-5"><Overview /></TabsContent>
        <TabsContent value="report" className="mt-5"><Report /></TabsContent>
        {Object.values(REGISTER_CONFIGS).map((c) => <TabsContent key={c.key} value={c.key} className="mt-5"><RegisterTab cfg={c} /></TabsContent>)}
        <TabsContent value="audit" className="mt-5"><Audit /></TabsContent>
      </Tabs>
    </div>
  );
}
